import { useCallback, useEffect, useRef, useState } from "react";
import { FiCheck, FiX } from "react-icons/fi";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";

import TimeOffService from "src/Services/API/timeoff.service";
import TimeOffDetail from "./TimeOffDetail";
import TimeOffTabbar, { FILTER_TABS, tabKeyFromPath } from "./TimeOffTabbar";
import TimeOffFilter, { rangeToParams } from "./TimeOffFilter";
import { handleError } from "src/Common/function";
import { getAvatarUrl, getImageUrl, parseLocalDate } from "src/Common/common";
import ConfirmDialog from "src/Common/confirmdialog";
import ApprovalPopover from "./ApprovalPopover";
import RevokeDialog from "./RevokeDialog";
import { TYPE_META, TimeOffTypeBadge } from "./TimeOffType";
import useTimeOffRealtime from "src/hooks/useTimeOffRealtime";

// Re-exported for existing importers; canonical source is ./TimeOffType.
export { TYPE_META };

// status: 0 pending, 1 approved with PTO, 2 approved without PTO, 3 rejected,
// 10 cancelled (by the requester)
export const STATUS_META = {
  0: {
    label: "Pending",
    className: "bg-[#FEF3C7] text-[#F59E0B] outline outline-1 outline-offset-[-1px] outline-[rgba(245,158,11,0.20)]",
  },
  1: {
    label: "Approved",
    className: "bg-[#D1FAE5] text-[#10B981] outline outline-1 outline-offset-[-1px] outline-[rgba(16,185,129,0.20)]",
  },
  2: {
    label: "Approved",
    className: "bg-[#D1FAE5] text-[#10B981] outline outline-1 outline-offset-[-1px] outline-[rgba(16,185,129,0.20)]",
  },
  3: {
    label: "Rejected",
    className: "bg-[#FEE2E2] text-[#EF4444] outline outline-1 outline-offset-[-1px] outline-[rgba(239,68,68,0.20)]",
  },
  10: {
    label: "Cancelled",
    className: "bg-[#F1F5F9] text-[#64748B] outline outline-1 outline-offset-[-1px] outline-[rgba(100,116,139,0.20)]",
  },
};

// "Oct 1, 2023"
export const formatDate = (value) => {
  if (!value) return "-";
  return parseLocalDate(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// "Oct 12 - Oct 15, 2023", or "Oct 20, 2023" for a single day.
// If the range spans two years, both years are shown: "Dec 30, 2023 - Jan 2, 2024".
export const formatDateRange = (start, end) => {
  if (!start) return "-";
  const s = parseLocalDate(start);
  const e = end ? parseLocalDate(end) : s;
  const withYear = { month: "short", day: "numeric", year: "numeric" };
  const noYear = { month: "short", day: "numeric" };
  const eStr = e.toLocaleDateString("en-US", withYear);
  if (s.toDateString() === e.toDateString()) return eStr;
  const sStr = s.toLocaleDateString("en-US", s.getFullYear() === e.getFullYear() ? noYear : withYear);
  return `${sStr} - ${eStr}`;
};

// inclusive day count between two dates
export const dayCount = (start, end) => {
  if (!start) return 0;
  const s = parseLocalDate(start);
  const e = end ? parseLocalDate(end) : s;
  const diff = Math.round((e - s) / (1000 * 60 * 60 * 24)) + 1;
  return diff > 0 ? diff : 1;
};

function TimeOff() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // id currently being updated, to disable its action buttons
  const [updatingId, setUpdatingId] = useState(null);
  // id of the request whose approval popover is open (null = none)
  const [approveId, setApproveId] = useState(null);
  const approveRef = useRef(null);
  // rejection confirmation modal: null = closed; { id } when shown
  const [confirm, setConfirm] = useState(null);
  // revoke confirmation modal: null = closed; { id } when shown
  const [revokeConfirm, setRevokeConfirm] = useState(null);

  // open the approval popover for a request
  const requestApprove = (id) => setApproveId((v) => (v === id ? null : id));

  // close the approval popover when clicking outside the trigger or popover
  // (the popover renders in a portal, so it's not a DOM child of the trigger)
  useEffect(() => {
    if (approveId == null) return;
    const onDown = (e) => {
      const inTrigger = approveRef.current && approveRef.current.contains(e.target);
      const inPopover = e.target.closest && e.target.closest("[data-approval-popover]");
      if (!inTrigger && !inPopover) setApproveId(null);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [approveId]);

  // open the rejection confirmation for a request
  const requestReject = (id) => {
    // defer so the originating click finishes bubbling before
    // OutsideClickHandler in ConfirmDialog mounts
    setTimeout(() => setConfirm({ id }), 0);
  };

  const confirmedRequest = confirm ? requests.find((r) => r.id === confirm.id) : null;

  // open the revoke confirmation for an approved request
  const requestRevoke = (id) => {
    setTimeout(() => setRevokeConfirm({ id }), 0);
  };

  const revokedRequest = revokeConfirm ? requests.find((r) => r.id === revokeConfirm.id) : null;

  // active tab is driven by the /time-off/:tab route param
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tabKeyFromPath(tab);
  const handleTabChange = (key) => navigate(`/time-off/${key}`);

  // request whose details are being viewed in the side drawer, or null
  const [detailItem, setDetailItem] = useState(null);
  // date-range filter (backend filters by these dates)
  const [dateRange, setDateRange] = useState("thisYear");
  const [page, setPage] = useState(1);

  const PAGE_SIZE = 10;

  const pendingCount = requests.filter((req) => req.status === 0).length;
  const activeStatuses = FILTER_TABS.find((t) => t.key === activeTab)?.statuses;
  const visibleRequests = activeStatuses ? requests.filter((req) => activeStatuses.includes(req.status)) : requests;

  const totalResults = visibleRequests.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pagedRequests = visibleRequests.slice(pageStart, pageStart + PAGE_SIZE);

  // reset to first page whenever the filter changes
  useEffect(() => {
    setPage(1);
  }, [activeTab, dateRange]);

  // `silent` skips the loading spinner — used by the realtime refresh so the
  // list updates in place without flashing "Loading...".
  const loadRequests = useCallback((rangeKey, { silent = false } = {}) => {
    if (!silent) setLoading(true);
    const params = rangeToParams(rangeKey);
    TimeOffService.getAll(params || undefined)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          const data = response.data;
          const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
          // requester-cancelled requests (status 10) are withdrawn — they don't
          // belong in the admin approval queue.
          setRequests(list.filter((req) => req.status !== 10));
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        if (!silent) setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadRequests(dateRange);
  }, [dateRange, loadRequests]);

  // Live-refresh when a request is submitted (frontend/technician app) or
  // decided elsewhere, so the queue stays current without a manual reload.
  const refreshRealtime = useCallback(() => {
    loadRequests(dateRange, { silent: true });
  }, [loadRequests, dateRange]);
  useTimeOffRealtime({ onRefresh: refreshRealtime });

  const updateStatus = (id, status, reason) => {
    setUpdatingId(id);
    TimeOffService.updateStatus(id, status, reason)
      .then((response) => {
        if (response.status === 200 || response.status === 201) {
          setRequests((prev) => prev.map((req) => (req.id === id ? { ...req, status } : req)));
          toast.success(
            status === 3
              ? "Request rejected"
              : status === 0
                ? "Approval revoked"
                : status === 1
                  ? "Request approved with PTO"
                  : status === 2
                    ? "Request approved without PTO"
                    : "Request approved",
          );
        }
      })
      .catch((error) => {
        handleError(error);
      })
      .finally(() => {
        setUpdatingId(null);
      });
  };

  return (
    <div className="w-full py-1">
      <div
        className="px-4 mb-5 flex items-center justify-between gap-4"
        style={{ borderBottom: "1px solid var(--Gray-200, #E5E8F3)" }}
      >
        <TimeOffTabbar activeTab={activeTab} onChange={handleTabChange} pendingCount={pendingCount} />
        <TimeOffFilter value={dateRange} onChange={setDateRange} />
      </div>

      {loading ? (
        <div className="text-[14px] text-borderColor">Loading...</div>
      ) : requests.length === 0 ? (
        <div className="text-[14px] text-borderColor">No time off requests yet.</div>
      ) : visibleRequests.length === 0 ? (
        <div className="text-[14px] text-borderColor">No requests in this category.</div>
      ) : (
        <div className="w-full flex flex-col items-start rounded-xl border border-[#F1F5F9] bg-white shadow-[0_4px_6px_-1px_rgba(0,0,0,0.05),0_2px_4px_-1px_rgba(0,0,0,0.03)] overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                {["Technician", "Type", "Dates", "Submitted", "Status", "Note"].map((label) => (
                  <th
                    key={label}
                    className="px-6 py-4 text-[12px] leading-4 font-InterBold text-[#64748B] uppercase tracking-[0.6px]"
                  >
                    {label}
                  </th>
                ))}
                <th className="px-6 py-4 text-right text-[12px] leading-4 font-InterBold text-[#64748B] uppercase tracking-[0.6px]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pagedRequests.map((req) => {
                const status = STATUS_META[req.status] || STATUS_META[0];
                const isPending = req.status === 0;
                const isUpdating = updatingId === req.id;
                const days = dayCount(req.startDate, req.endDate);
                return (
                  <tr
                    key={req.id}
                    onClick={() => setDetailItem(req)}
                    className="border-t border-[#F1F5F9] cursor-pointer hover:bg-[#F8FAFC]"
                  >
                    {/* Technician */}
                    <td className="px-6 py-3">
                      <div className="flex items-center gap-3">
                        <img
                          src={getAvatarUrl(req.user?.avatar)}
                          className="w-9 h-9 rounded-full border border-[#E2E8F0] object-cover"
                          alt=""
                        />
                        <div className="flex flex-col">
                          <span className="text-[14px] font-InterMedium text-[#1E293B] leading-5">
                            {req.user?.name || req.user?.email || "-"}
                          </span>
                          <span className="text-[12px] font-InterRegular text-[#64748B] leading-4">
                            {req.user?.roles?.[0]?.name || "Technician"}
                          </span>
                        </div>
                      </div>
                    </td>
                    {/* Type */}
                    <td className="px-6 py-3">
                      <TimeOffTypeBadge type={req.type} />
                    </td>
                    {/* Dates */}
                    <td className="px-6 py-3">
                      <div className="flex flex-col">
                        <span className="text-[14px] font-InterMedium text-[#334155] leading-5">
                          {formatDateRange(req.startDate, req.endDate)}
                        </span>
                        <span className="text-[12px] font-InterRegular text-[#64748B] leading-4">
                          {days} {days === 1 ? "day" : "days"}
                        </span>
                      </div>
                    </td>
                    {/* Submitted */}
                    <td className="px-6 py-3 text-[14px] font-InterRegular text-[#475569] leading-5">
                      {formatDate(req.createdAt)}
                    </td>
                    {/* Status */}
                    <td className="px-6 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-[12px] font-InterSemiBold leading-4 ${status.className}`}
                      >
                        {status.label}
                      </span>
                    </td>
                    {/* Note */}
                    <td className="px-6 py-3 max-w-[150px]">
                      <div className="truncate text-[12px] font-InterRegular text-[#475569] leading-4">
                        {req.notes || "-"}
                      </div>
                    </td>
                    {/* Actions */}
                    <td className="px-6 py-3" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end items-center gap-2">
                        {isPending && (
                          <>
                            <button
                              ref={approveId === req.id ? approveRef : null}
                              disabled={isUpdating}
                              onClick={() => requestApprove(req.id)}
                              className="px-3 py-1.5 rounded-lg border border-[#E2E8F0] bg-white text-[12px] font-InterSemiBold text-[#1E293B] leading-4 tracking-[0.15px] disabled:opacity-50 flex items-center gap-1.5 focus:outline-none"
                            >
                              <FiCheck size={14} className="text-[#1E293B]" />
                              Approve
                            </button>
                            {approveId === req.id && (
                              <ApprovalPopover
                                anchorRef={approveRef}
                                onConfirm={(status) => {
                                  updateStatus(req.id, status);
                                  setApproveId(null);
                                }}
                              />
                            )}
                            <button
                              disabled={isUpdating}
                              onClick={() => requestReject(req.id)}
                              className="px-3 py-1.5 rounded-lg outline outline-1 outline-offset-[-1px] outline-[#FECACA] text-[12px] font-InterSemiBold text-[#DC2626] leading-4 tracking-[0.08px] disabled:opacity-50 flex items-center gap-1.5"
                            >
                              <FiX size={14} className="text-[#DC2626]" />
                              Reject
                            </button>
                          </>
                        )}
                        {(req.status === 1 || req.status === 2) && (
                          <>
                            <span className="font-OpenSansRegular text-[12px] italic leading-4 text-[#7C829A]">
                              {req.status === 1 ? "Approved w/ PTO" : "Approved w/o PTO"}
                            </span>
                            <button
                              disabled={isUpdating}
                              onClick={() => requestRevoke(req.id)}
                              className="flex h-8 items-center justify-center gap-2 px-3 rounded-lg border border-[#C9CEE2] bg-white shadow-[0_1px_2px_0_rgba(16,24,40,0.05)] font-OpenSansSemiBold text-[14px] leading-5 tracking-[0.14px] capitalize text-[#1A2332] disabled:opacity-50 focus:outline-none"
                            >
                              Revoke
                            </button>
                          </>
                        )}
                        {/* <button
                          onClick={() => setDetailItem(req)}
                          className="p-1.5 rounded-md hover:bg-[#F1F5F9]"
                          title="View details"
                        >
                          <img src={getImageUrl("/images/common/eye.svg")} className="w-[18px] h-[18px]" alt="View" />
                        </button> */}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

            <div className="w-full flex justify-between items-center px-6 py-4 border-t border-[#E2E8F0]">
              <div className="text-[14px] font-InterRegular text-[#64748B] leading-5">
                Showing{" "}
                <span className="font-InterMedium text-[#1E293B]">{totalResults === 0 ? 0 : pageStart + 1}</span> to{" "}
                <span className="font-InterMedium text-[#1E293B]">{pageStart + pagedRequests.length}</span> of{" "}
                <span className="font-InterMedium text-[#1E293B]">{totalResults}</span> results
              </div>

              <div className="flex items-center gap-1">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 rounded-md bg-[#F8FAFC] outline outline-1 outline-offset-[-1px] outline-[#E2E8F0] text-[14px] font-InterRegular text-[#64748B] leading-5 disabled:opacity-50"
                >
                  Previous
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((num) => {
                  const isActive = num === currentPage;
                  return (
                    <button
                      key={num}
                      onClick={() => setPage(num)}
                      className={`px-3 py-1.5 rounded-md text-[14px] leading-5 ${
                        isActive
                          ? "bg-white outline outline-1 outline-offset-[-1px] outline-[#E2E8F0] font-InterMedium text-[#334155]"
                          : "font-InterRegular text-[#64748B]"
                      }`}
                    >
                      {num}
                    </button>
                  );
                })}

                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 rounded-md bg-[#F8FAFC] outline outline-1 outline-offset-[-1px] outline-[#E2E8F0] text-[14px] font-InterRegular text-[#334155] leading-5 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

      <TimeOffDetail
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onApprove={(req, status) => {
          setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status } : r)));
          toast.success(status === 1 ? "Request approved with PTO" : "Request approved without PTO");
        }}
        onReject={(req) => {
          setRequests((prev) => prev.map((r) => (r.id === req.id ? { ...r, status: 3 } : r)));
          toast.success("Request rejected");
        }}
      />

      <ConfirmDialog
        shown={!!confirm}
        title="Reject this request?"
        description={`This will reject ${confirmedRequest?.user?.name || "the technician"}'s time-off request.`}
        onConfirmClicked={() => {
          if (!confirm) return;
          updateStatus(confirm.id, 3);
          setConfirm(null);
        }}
        onCancelClicked={() => setConfirm(null)}
      />

      <RevokeDialog
        shown={!!revokeConfirm}
        technicianName={revokedRequest?.user?.name}
        submitting={updatingId === revokeConfirm?.id}
        onConfirm={(notes) => {
          if (!revokeConfirm) return;
          updateStatus(revokeConfirm.id, 3, notes || undefined);
          setRevokeConfirm(null);
        }}
        onCancel={() => setRevokeConfirm(null)}
      />
    </div>
  );
}

export default TimeOff;
